import type { ReactNode } from 'react';
import { Skeleton } from '@reflex/ui/components/ui/skeleton';
import { PageLayout, PageHeader } from '@/components/layout/PageLayout';
import { NestedDetailHeader } from './NestedDetailHeader';

interface BackLinkConfig {
  to: string;
  label?: string;
  ariaLabel?: string;
}

interface DetailPageShellProps<T> {
  /** Title rendered in the loading / error fallbacks. */
  title: string;
  /** Description shown while the resource is loading. */
  loadingDescription?: string;
  /** Description shown when the resource fails to load. */
  errorDescription?: string;
  /** Optional nested back-link rendered via `NestedDetailHeader` in the error state. */
  back?: BackLinkConfig;
  /** Whether the underlying query is still loading. */
  isLoading: boolean;
  /** Whether the underlying query errored. */
  isError: boolean;
  /** The resolved resource, or `undefined`/`null` when unavailable. */
  data: T | undefined | null;
  /** Renders the page body when `data` is available. */
  children: (resource: T) => ReactNode;
}

/**
 * Standard chrome for detail editor pages.
 *
 * Renders the `loading → error → not-found → data` waterfall behind a
 * single declarative shell so each detail page only writes the success
 * branch. The error and not-found branches share copy and an optional
 * back-link so every detail page has consistent affordances.
 */
export function DetailPageShell<T>({
  title,
  loadingDescription = 'Loading…',
  errorDescription = 'This resource could not be loaded.',
  back,
  isLoading,
  isError,
  data,
  children,
}: DetailPageShellProps<T>) {
  if (isLoading) {
    return (
      <PageLayout>
        <PageHeader title={title} description={loadingDescription} />
        <div className="space-y-4">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </PageLayout>
    );
  }

  if (isError || !data) {
    return (
      <PageLayout>
        {back ? (
          <NestedDetailHeader back={back} title={title} description={errorDescription} />
        ) : (
          <PageHeader title={title} description={errorDescription} />
        )}
      </PageLayout>
    );
  }

  return <>{children(data)}</>;
}
